import { isRuleGraphPipelineKind } from "@intelligems/ig-types";

import type { RuleGraphAnalysis } from "./analysis";
import type { LayoutGroup } from "./grouped-layout";
import type { RuleGraphFlowEdge } from "./ruleGraphNodeData/RuleGraphFlowEdge";
import type { RuleGraphFlowNode } from "./ruleGraphNodeData/RuleGraphFlowNode";
import type { RuleGraphOverlaysByNodeId } from "./types";
import type { GraphLayoutRequest } from "./useRuleGraphLayout";

function buildLayoutGroups(analysis: RuleGraphAnalysis): LayoutGroup[] {
  const { graph } = analysis;

  if (analysis.mode === "base") {
    return graph.nodes
      .filter((node) => node.kind === "branch")
      .map((node) => ({
        id: node.id,
        nodeIds: [
          node.id,
          ...graph.nodes
            .filter(
              (member) =>
                member.branchId === node.id &&
                isRuleGraphPipelineKind(member.kind)
            )
            .map((member) => member.id),
        ],
      }));
  }

  const inputNodeId = graph.nodes.find((node) => node.kind === "input")!.id;
  if (analysis.mode === "branchless") {
    return [
      {
        id: "pipeline",
        headerId: inputNodeId,
        nodeIds: [inputNodeId, ...analysis.tailNodeIds],
      },
    ];
  }

  const leadInGroup: LayoutGroup[] = analysis.leadInNodeIds.length
    ? [
        {
          id: "lead-in",
          headerId: inputNodeId,
          nodeIds: [inputNodeId, ...analysis.leadInNodeIds],
        },
      ]
    : [];

  return [
    ...leadInGroup,
    ...analysis.laneOrder.map((id) => ({
      id,
      headerId: id,
      nodeIds: [id, ...(analysis.pipelineByBranchId.get(id) ?? [])],
    })),
  ];
}

export function buildGraphLayoutRequest({
  analysis,
  nodes,
  edges,
  measurements = {},
  overlaysByNodeId,
  skeleton = false,
}: {
  analysis: RuleGraphAnalysis;
  nodes: RuleGraphFlowNode[];
  edges: RuleGraphFlowEdge[];
  measurements?: Record<string, { width: number; height: number }>;
  overlaysByNodeId?: RuleGraphOverlaysByNodeId;
  skeleton?: boolean;
}): GraphLayoutRequest {
  const graph = analysis.graph;
  const boxes = nodes.map((node) => ({
    id: node.id,
    width: node.width!,
    height: node.height!,
    header: node.type === "ruleGraphNode" && node.data.isLaneHeader === true,
    ...(!skeleton &&
    (overlaysByNodeId?.[node.id]?.result ||
      overlaysByNodeId?.[node.id]?.cartInput)
      ? measurements[node.id]
      : undefined),
  }));
  const visibleIds = new Set(boxes.map((box) => box.id));
  const groups = buildLayoutGroups(analysis);
  const requestEdges = edges.map(
    ({ id, source, target, sourceHandle, targetHandle }) => ({
      id,
      source,
      target,
      sourceHandle,
      targetHandle,
    })
  );
  const graphKey = JSON.stringify([
    graph.nodes.map((node) => [node.id, node.kind, node.branchId]),
    graph.edges,
    [...visibleIds],
    groups,
  ]);
  const sharedTailNodeIds =
    analysis.mode === "branched"
      ? [
          ...analysis.tailNodeIds,
          ...graph.nodes
            .filter((node) => node.kind === "output")
            .map((node) => node.id),
        ].filter((id) => visibleIds.has(id))
      : [];
  return { boxes, edges: requestEdges, groups, graphKey, sharedTailNodeIds };
}
